
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

export type RsvpStatus = 'invited' | 'accepted' | 'declined' | 'maybe';

export interface RsvpDetails {
  invite_id?: string;
  selected_dates?: string[] | null;
  selected_times?: string[] | null;
  feedback_text?: string | null;
  rsvp_status?: RsvpStatus;
}

export const useRsvpStatus = (eventId: string | undefined, hasResponded: boolean) => {
  // Query to fetch RSVP details for the current user
  const { data: rsvpDetails } = useQuery({
    queryKey: ['user-rsvp-details', eventId],
    queryFn: async () => {
      if (!eventId) return null;
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return null;
      }
      
      const { data: userInvite } = await supabase
        .from('event_invites')
        .select(`
          invite_id,
          selected_dates,
          selected_times,
          feedback_text,
          rsvp_status
        `)
        .eq('event_id', eventId)
        .eq('claimed_by_user_id', user.id)
        .maybeSingle();
      
      if (userInvite) {
        return userInvite;
      }
      
      const { data: userProfile } = await supabase
        .from('users')
        .select('phone_number')
        .eq('user_id', user.id)
        .single();
        
      if (!userProfile?.phone_number) {
        return null;
      }
      
      const { data: phoneInvite } = await supabase
        .from('event_invites')
        .select(`
          invite_id,
          selected_dates,
          selected_times,
          feedback_text,
          rsvp_status
        `)
        .eq('event_id', eventId)
        .eq('phone_number', userProfile.phone_number)
        .maybeSingle();
        
      return phoneInvite;
    },
    enabled: !!eventId && hasResponded
  });
  
  // Query to check if RSVP updates are allowed
  const { data: rsvpUpdateAllowed, isLoading: checkingRsvpStatus } = useQuery({
    queryKey: ['can-update-rsvp', eventId],
    queryFn: async () => {
      if (!eventId) return false;
      
      // Get event status
      const { data: event } = await supabase
        .from('events')
        .select('status')
        .eq('event_id', eventId)
        .single();
        
      // If event is not in "planned" status, RSVP updates are not allowed
      if (!event || event.status !== 'planned') {
        return false;
      }
      
      // Check if there are any date recommendations
      const { data: dateRecs, error: dateError } = await supabase
        .from('event_date_recommendations')
        .select('recommendation_id')
        .eq('event_id', eventId)
        .limit(1);
        
      if (dateError) console.error('Error checking date recommendations:', dateError);
      
      // Check if there are any venue recommendations
      const { data: venueRecs, error: venueError } = await supabase
        .from('event_venue_recommendations')
        .select('recommendation_id')
        .eq('event_id', eventId)
        .limit(1);
        
      if (venueError) console.error('Error checking venue recommendations:', venueError);
      
      // If there are no recommendations, RSVP updates are allowed
      return (!dateRecs || dateRecs.length === 0) && 
             (!venueRecs || venueRecs.length === 0);
    },
    enabled: !!eventId
  });

  // Helper functions for formatting data
  const formatDates = (dates?: string[] | null) => {
    if (!dates || dates.length === 0) return null;
    
    return dates.map(date => {
      try {
        return format(new Date(date), 'EEE, dd MMM yyyy');
      } catch (e) {
        return date;
      }
    });
  };
  
  const formatTimeOfDay = (times?: string[] | null) => {
    if (!times || times.length === 0) return null;
    
    return times.map(time => {
      return time.charAt(0).toUpperCase() + time.slice(1);
    });
  };

  const displayDates = formatDates(rsvpDetails?.selected_dates);
  const displayTimes = formatTimeOfDay(rsvpDetails?.selected_times);

  return {
    rsvpDetails,
    rsvpUpdateAllowed,
    checkingRsvpStatus,
    displayDates,
    displayTimes
  };
};
